import { px } from '@adsk/alloy-react-helpers';

import {
  AlloyLegacyThemeDesignTokens,
  ColorPickerDesignTokens,
  WeaveTheme,
} from '../types';
import { buildBoxShadow } from './common';

type WeaveTokens = NonNullable<WeaveTheme['tokens']>;

export const getColorPickerTokensAlloy = (
  theme: AlloyLegacyThemeDesignTokens,
): ColorPickerDesignTokens => {
  const { tokens, borderRadius, spacing, halos, shadows, typography } =
    theme ?? {};
  const { colors } = tokens ?? {};

  return {
    swatch: {
      _: {
        'border-radius': borderRadius.circle,
        height: px(24),
        width: px(24),
        'min-height': px(24),
        'min-width': px(24),
        'border-width': '1px',
        'border-style': 'solid',
        cursor: 'pointer',
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'center',
        padding: '0',
        overflow: 'hidden',
        outline: 'none',
        transition: theme.transitions.create(['box-shadow', 'border-color']),
        '&:hover': {
          'box-shadow': halos.hover,
        },
        '&:focus-within': {
          'box-shadow': halos.focus,
        },
        '&:active': {
          'box-shadow': halos.active,
        },
      },
      idle: {},
      selected: {
        '&:not(&:active)': {
          'box-shadow': `0 0 0 2px ${colors?.border.selected.value}`,
        },
      },
      highlighted: {
        'box-shadow': halos.focus,
      },
      disabled: {
        cursor: 'not-allowed',
        'box-shadow': 'none',
      },
      transparent: {
        background: colors?.background.primary.value,
        icon: { fill: colors?.background.error.value },
      },
      border: {
        light: colors?.border.subtle.value,
        dark: colors?.border.inverse.value,
      },
    },
    palette: {
      background: colors?.background.primary.value,
      'border-radius': px(borderRadius.medium),
      'box-shadow': shadows.highElevation,
      padding: px(spacing.medium),
      display: 'grid',
      'place-content': 'center',
      gap: px(spacing.XS),
      swatchMargin: px(spacing.XXS),
    },
    control: {
      _: {
        display: 'flex',
        'justify-content': 'space-between',
        'align-items': 'center',
        color: 'inherit',
        background: colors?.background.primary.value,
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': colors?.border.value,
        'border-radius': px(borderRadius.small),
        cursor: 'pointer',
        outline: 'none',
        width: px(344),
        height: px(36),
        padding: `1px ${spacing.S}px`,
        'font-family': typography.labelMedium.fontFamily,
        'font-size': typography.labelMedium.fontSize,
        'font-weight': String(typography.labelMedium.fontWeight),
        'line-height': typography.labelMedium.lineHeight,
      },
      open: {
        true: {
          'border-color': colors?.border.focused.value,
          'box-shadow': halos.focus,
          '&:hover': {
            'box-shadow': halos.focus,
          },
        },
        false: {
          transition: theme.transitions.create(['box-shadow', 'border-color']),
          '&:hover': {
            'box-shadow': halos.hover,
          },
          '&:focus-within': {
            'box-shadow': halos.focus,
          },
          '&:active': {
            'box-shadow': halos.active,
            'border-color': theme.colors.charcoal500,
          },
        },
      },
      placeholder: {
        true: { color: colors?.text.tertiary.value },
        false: {},
      },
      disabled: {
        true: {
          background: colors?.background.neutral.inactive.value,
          'border-color': colors?.border.inactive.value,
          color: colors?.text.inactive.value,
          cursor: 'not-allowed',
          'box-shadow': 'none',
        },
        false: {},
      },
      badge: {
        _: {
          'margin-right': px(spacing.XS),
        },
        line: {
          width: px(6),
          height: px(20),
          'min-width': px(4),
          'border-radius': px(borderRadius.medium),
        },
        dot: {
          width: px(16),
          height: px(16),
        },
      },
      icon: {
        _: {
          color: colors?.icon.primary.value,
          'margin-left': px(spacing.XS),
          'margin-right': px(spacing.XXS),
        },
        open: {
          false: {},
          true: { transform: 'rotate(180deg)' },
        },
      },
      ellipsis: {
        overflow: 'hidden',
        'text-overflow': 'ellipsis',
        'white-space': 'nowrap',
      },
    },
  };
};

export const getColorPickerTokensWeave = (
  tokens: WeaveTokens,
  fontFamily: string,
): ColorPickerDesignTokens => {
  const { semantic, generic } = tokens ?? {};

  const white = generic?.color?.white?.[100];
  const popover = tokens?.component?.popover;
  const checkbox = tokens?.component?.checkbox;
  const input = tokens?.component?.input;

  const borderSelected = semantic?.['border-color']?.selected;
  const bgPrimary = white;
  const bgError = semantic?.['background-color']?.alert?.error;
  const borderSubtle = semantic?.['border-color']?.divider?.light;
  const borderInverse = semantic?.['border-color']?.heavy?.inverse;
  const borderDefault = semantic?.['border-color']?.heavy?.default;
  const borderFocus = input?.['border-bottom-color']?.focus;
  const bgInactive = input?.['background-color']?.selected;
  const borderInactive = input?.['border-bottom-color']?.hover;
  const textInactive = semantic?.['text-color']?.light;
  const textTertiary = semantic?.['text-color']?.placeholder;
  const iconPrimary = semantic?.['icon-color']?.medium;

  const transition = `box-shadow ${input?.['transition-duration'] || '200ms'} ${input?.['transition-timing-function'] || 'ease'}, border-color ${input?.['transition-duration'] || '200ms'} ${input?.['transition-timing-function'] || 'ease'}`;

  const haloFocus = buildBoxShadow(checkbox, 'focus');
  const haloHover = buildBoxShadow(checkbox, 'hover');
  const haloActive = buildBoxShadow(checkbox, 'pressed');

  return {
    swatch: {
      _: {
        'border-radius': '50%',
        height: px(24),
        width: px(24),
        'min-height': px(24),
        'min-width': px(24),
        'border-width': '1px',
        'border-style': 'solid',
        cursor: 'pointer',
        display: 'flex',
        'align-items': 'center',
        'justify-content': 'center',
        padding: '0',
        overflow: 'hidden',
        outline: 'none',
        transition,
        '&:hover': {
          'box-shadow': haloHover,
        },
        '&:focus-within': {
          'box-shadow': haloFocus,
        },
        '&:active': {
          'box-shadow': haloActive,
        },
      },
      idle: {},
      selected: {
        '&:not(&:active)': {
          'box-shadow': `0 0 0 2px ${borderSelected}`,
        },
      },
      highlighted: {
        'box-shadow': haloFocus,
      },
      disabled: {
        cursor: 'not-allowed',
        'box-shadow': 'none',
      },
      transparent: {
        background: bgPrimary,
        icon: { fill: bgError },
      },
      border: {
        light: borderSubtle,
        dark: borderInverse,
      },
    },
    palette: {
      background: bgPrimary,
      'border-radius': generic?.['border-radius']?.[4],
      'box-shadow': `${popover?.['box-shadow-x'] || '0px'} ${popover?.['box-shadow-y'] || '0px'} ${popover?.['box-shadow-blur'] || '0px'} ${popover?.['box-shadow-color'] || 'transparent'}`,
      padding: generic?.spacing?.[16],
      display: 'grid',
      'place-content': 'center',
      gap: generic?.spacing?.[8],
      swatchMargin: generic?.spacing?.[4],
    },
    control: {
      _: {
        display: 'flex',
        'justify-content': 'space-between',
        'align-items': 'center',
        color: 'inherit',
        background: bgPrimary,
        'border-width': '1px',
        'border-style': 'solid',
        'border-color': borderDefault,
        'border-radius': generic?.['border-radius']?.[2],
        cursor: 'pointer',
        outline: 'none',
        width: px(344),
        height: px(36),
        padding: `1px ${generic?.spacing?.[12]}`,
        'font-family': fontFamily,
        'font-size': input?.['font-size'],
        'font-weight': input?.['font-weight'],
        'line-height': input?.['line-height'],
      },

      open: {
        true: {
          'border-color': borderFocus,
          'box-shadow': haloFocus,
          '&:hover': {
            'box-shadow': haloFocus,
          },
        },
        false: {
          transition,
          '&:hover': {
            'box-shadow': haloHover,
          },
          '&:focus-within': {
            'box-shadow': haloFocus,
          },
          '&:active': {
            'box-shadow': haloActive,
          },
        },
      },
      placeholder: {
        true: {
          color: textTertiary,
        },
        false: {},
      },
      disabled: {
        true: {
          background: bgInactive,
          'border-color': borderInactive,
          color: textInactive,
          cursor: 'not-allowed',
          'box-shadow': 'none',
        },
        false: {},
      },
      badge: {
        _: { 'margin-right': generic?.spacing?.[8] },
        line: {
          width: px(6),
          height: px(20),
          'min-width': px(4),
          'border-radius': generic?.['border-radius']?.[4],
        },
        dot: {
          width: px(16),
          height: px(16),
        },
      },
      icon: {
        _: {
          color: iconPrimary,
          'margin-left': generic?.spacing?.[8],
          'margin-right': generic?.spacing?.[4],
        },
        open: {
          false: {},
          true: {
            transform: 'rotate(180deg)',
          },
        },
      },
      ellipsis: {
        overflow: 'hidden',
        'text-overflow': 'ellipsis',
        'white-space': 'nowrap',
      },
    },
  };
};
